import { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany } from '@/contexts/CompanyContext';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  Filter,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Receipt,
  Users,
  Maximize2,
  Minimize2,
  FileText,
  Settings2,
  Download,
  Link2Off,
  Banknote,
  CircleDashed,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  CreditCard,
  Wallet,
} from 'lucide-react';
import { CompactKpiCard } from '@/components/kpi-card';
import type { Expense } from '@/types';
import { format } from 'date-fns';
import { ApprovalHistoryTimeline } from '../time/approval-history-timeline';

// DataTable + pagination
import { DataTableEnhanced, type ColumnDef, type ColumnSort } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray } from '@/types/pagination';
import type { PaginationMetadata } from '@/types/pagination';

// Column configurator + preferences
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { MultiSelect } from '@/components/ui/multiselect';
import { useColumnPreferences } from '@/hooks/use-column-preferences';

// Advanced filter sheet
import {
  ExpenseEntriesFilterSheet,
  type ExpenseFilters,
  countActiveExpenseFilters,
  applyExpenseFilters,
} from './expense-entries-filter-sheet';

// Group-by utilities
import {
  groupExpenses,
  EX_GROUP_BY_OPTIONS,
  type EXGroupById,
  type ExpenseGroup,
} from '@/lib/expense-group-by-utils';

interface ExpenseEntriesTabProps {
  projectId: number;
  projectCode?: string;
  expenses: Expense[];
  employees: Array<{ id: number; name: string }>;
  isExpenseConfigured?: boolean;
}

interface ExpenseSummary {
  totalAmount: number;
  totalCount: number;
  approvedAmount: number;
  approvedCount: number;
  pendingAmount: number;
  pendingCount: number;
  uniqueEmployees: number;
}

// ─── Column definitions ────────────────────────────────────────────────────────
const DEFAULT_EXPENSE_COLUMNS: ColumnDefinition[] = [
  { id: 'date',            name: 'Date',        description: 'Expense date',                              category: 'Timeline',  visible: true,  locked: true,  order: 0  },
  { id: 'employee',        name: 'Employee',    description: 'Employee name',                             category: 'Resource',  visible: true,  locked: true,  order: 1  },
  { id: 'type',            name: 'Type',        description: 'Expense type / category',                   category: 'Status',    visible: true,                 order: 2  },
  { id: 'category',        name: 'Category',    description: 'Credit Card or Expense',                    category: 'Status',    visible: false,                order: 3  },
  { id: 'description',     name: 'Description',   description: 'Expense description',                       category: 'Status',    visible: true,                 order: 4  },
  { id: 'costCentre',      name: 'Cost Centre',  description: 'Cost centre for financial rollups',          category: 'Financial', visible: false,                order: 5  },
  { id: 'status',          name: 'Status',        description: 'Approval status',                           category: 'Status',    visible: true,  locked: true,  order: 6  },
  { id: 'net',             name: 'Net',         description: 'Net amount excluding VAT',                  category: 'Financial', visible: false,                order: 7  },
  { id: 'vat',             name: 'VAT',         description: 'VAT amount',                                category: 'Financial', visible: false,                order: 8  },
  { id: 'vatCode',         name: 'VAT Code',    description: 'VAT / tax code applied to the expense',    category: 'Financial', visible: false,                order: 9  },
  { id: 'amount',          name: 'Amount',      description: 'Expense amount',                            category: 'Financial', visible: true,  locked: true,  order: 10 },
  { id: 'receipt',         name: 'Receipt',     description: 'Receipt attachment',                        category: 'Status',    visible: false,                order: 11 },
];

export function ExpenseEntriesTab({
  projectId,
  projectCode,
  expenses,
  employees,
  isExpenseConfigured,
}: ExpenseEntriesTabProps) {
  const { currentCompany } = useCompany();

  // ── UI state ──────────────────────────────────────────────────────────
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [approvalHistorySheetOpen, setApprovalHistorySheetOpen] = useState(false);
  const [approvalHistoryExpense, setApprovalHistoryExpense] = useState<Expense | null>(null);
  const [approvalHistoryExpanded, setApprovalHistoryExpanded] = useState(false);

  // ── Search / filter state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<ExpenseFilters>({});
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // ── Quick filters ─────────────────────────────────────────────────────
  const [quickStatusFilter, setQuickStatusFilter] = useState<string[]>([]);
  const [quickEmployeeFilter, setQuickEmployeeFilter] = useState<string[]>([]);
  const [quickTypeFilter, setQuickTypeFilter] = useState<string[]>([]);

  // ── Table state ───────────────────────────────────────────────────────
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: 'date', direction: 'desc' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // ── Group By state ────────────────────────────────────────────────────
  const [groupBy, setGroupBy] = useState<EXGroupById | ''>('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // ── Column preferences ────────────────────────────────────────────────
  const { columns: expenseColumns, updateColumns } = useColumnPreferences({
    storageKey: 'expense-entries-columns',
    defaultColumns: DEFAULT_EXPENSE_COLUMNS,
  });

  const visibleColumns = useMemo(
    () => expenseColumns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [expenseColumns]
  );

  const getEmployeeName = (expense: Expense): string => {
    if (expense.employeeName) return expense.employeeName;
    const employee = employees.find((e) => e.id === expense.employeeId);
    return employee?.name || 'Unknown Employee';
  };

  // ── Derived option lists ──────────────────────────────────────────────
  const availableEmployees = useMemo(() => {
    const ids = Array.from(new Set(expenses.map((e) => e.employeeId)));
    return ids
      .map((id) => {
        const ex = expenses.find((e) => e.employeeId === id);
        return { value: String(id), label: ex ? getEmployeeName(ex) : 'Unknown Employee' };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [expenses, employees]);

  // Type = expense.category (used in "Type" column and filter)
  const availableTypes = useMemo(() => {
    const vals = Array.from(new Set(expenses.map((e) => e.category))).sort();
    return vals.map((v) => ({ value: v, label: v }));
  }, [expenses]);

  // Category = expense.paymentMethod (used in "Category" column and filter)
  const availableCategories = useMemo(() => {
    const vals = Array.from(
      new Set(expenses.map((e) => e.paymentMethod).filter((v): v is string => !!v))
    ).sort();
    return vals.map((v) => ({ value: v, label: v }));
  }, [expenses]);

  // VAT Code = expense.taxCodeId
  const availableVatCodes = useMemo(() => {
    const vals = Array.from(
      new Set(expenses.map((e) => e.taxCodeId).filter((v): v is string => !!v))
    ).sort();
    return vals.map((v) => ({ value: v, label: v }));
  }, [expenses]);

  // Cost Centre = expense.costCentreId
  const availableCostCentres = useMemo(() => {
    const vals = Array.from(
      new Set(expenses.map((e) => e.costCentreId).filter((v): v is string => !!v))
    ).sort();
    return vals.map((v) => ({ value: v, label: v }));
  }, [expenses]);

  // ── Quick filter option lists ─────────────────────────────────────────
  const STATUS_OPTIONS = useMemo(() => {
    const seen = new Set<string>();
    expenses.forEach((e) => seen.add(e.approved ? 'Approved' : 'Pending'));
    return Array.from(seen).sort().map((s) => ({ value: s, label: s }));
  }, [expenses]);

  const EMPLOYEE_OPTIONS = useMemo(() => availableEmployees, [availableEmployees]);

  const TYPE_OPTIONS = useMemo(() => availableTypes, [availableTypes]);

  // ── Summary ───────────────────────────────────────────────────────────
  const summary: ExpenseSummary = useMemo(() => {
    const approved = expenses.filter((e) => e.approved);
    const pending   = expenses.filter((e) => !e.approved);
    return {
      totalAmount:     expenses.reduce((s, e) => s + e.amount, 0),
      totalCount:      expenses.length,
      approvedAmount:  approved.reduce((s, e) => s + e.amount, 0),
      approvedCount:   approved.length,
      pendingAmount:   pending.reduce((s, e) => s + e.amount, 0),
      pendingCount:    pending.length,
      uniqueEmployees: new Set(expenses.map((e) => e.employeeId)).size,
    };
  }, [expenses]);

  // ── Filter + sort + paginate ──────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = [...expenses];

    // 1. Text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.description.toLowerCase().includes(q) ||
          e.category.toLowerCase().includes(q) ||
          getEmployeeName(e).toLowerCase().includes(q) ||
          e.date.includes(q) ||
          (e.merchantName && e.merchantName.toLowerCase().includes(q)) ||
          (e.costCentreId && e.costCentreId.toLowerCase().includes(q))
      );
    }

    // 2. Quick filters
    if (quickStatusFilter.length > 0) {
      result = result.filter((e) =>
        quickStatusFilter.includes(e.approved ? 'Approved' : 'Pending')
      );
    }
    if (quickEmployeeFilter.length > 0) {
      result = result.filter((e) => quickEmployeeFilter.includes(String(e.employeeId)));
    }
    if (quickTypeFilter.length > 0) {
      result = result.filter((e) => quickTypeFilter.includes(e.category));
    }

    // 3. Advanced filters
    result = applyExpenseFilters(result, advancedFilters);
    return result;
  }, [expenses, searchQuery, quickStatusFilter, quickEmployeeFilter, quickTypeFilter, advancedFilters]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    for (const sort of [...columnSorts].reverse()) {
      result.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        switch (sort.columnId) {
          case 'date':     aVal = new Date(a.date).getTime(); bVal = new Date(b.date).getTime(); break;
          case 'employee': aVal = getEmployeeName(a); bVal = getEmployeeName(b); break;
          case 'amount':   aVal = a.amount; bVal = b.amount; break;
          case 'category': aVal = a.category; bVal = b.category; break;
          default:         aVal = String((a as unknown as Record<string, unknown>)[sort.columnId] ?? ''); bVal = String((b as unknown as Record<string, unknown>)[sort.columnId] ?? '');
        }
        const cmp = typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
        return sort.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [filtered, columnSorts]);

  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(sorted.length, currentPage, itemsPerPage),
    [sorted.length, currentPage, itemsPerPage]
  );

  const paginatedExpenses = useMemo(
    () => paginateArray(sorted, currentPage, itemsPerPage).data,
    [sorted, currentPage, itemsPerPage]
  );

  // Full sorted list used for prev/next sheet navigation (not paginated)
  const allSortedExpenses = sorted;

  useEffect(() => { setCurrentPage(1); }, [searchQuery, quickStatusFilter, quickEmployeeFilter, quickTypeFilter, advancedFilters, columnSorts, itemsPerPage, groupBy]);

  // ── Grouped data ──────────────────────────────────────────────────────
  const groupedData = useMemo((): ExpenseGroup[] | null => {
    if (!groupBy) return null;
    return groupExpenses(sorted, groupBy, getEmployeeName);
  }, [sorted, groupBy, employees]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

  // ── Column defs ───────────────────────────────────────────────────────
  const columnDefs = useMemo((): ColumnDef<Expense, unknown>[] => {
    return visibleColumns.map((col): ColumnDef<Expense, unknown> => {
      switch (col.id) {
        case 'date':
          return {
            id: 'date',
            header: col.name,
            accessorFn: (r) => r.date,
            cell: ({ row }) => (
              <button
                onClick={() => setSelectedExpense(row.original)}
                className="text-primary hover:underline focus:outline-none rounded font-medium"
              >
                {format(new Date(row.original.date), 'dd MMM yyyy')}
              </button>
            ),
          };

        case 'employee':
          return {
            id: 'employee',
            header: col.name,
            accessorFn: (r) => getEmployeeName(r),
          };

        case 'type':
          return {
            id: 'type',
            header: col.name,
            accessorFn: (r) => r.category,
            cell: ({ row }) => <Badge variant="outline">{row.original.category}</Badge>,
          };

        case 'category':
          return {
            id: 'category',
            header: col.name,
            enableSorting: false,
            accessorFn: (r) => r.paymentMethod ?? '',
            cell: ({ row }) => {
              const method = row.original.paymentMethod;
              if (!method) return <span className="text-muted-foreground text-sm">—</span>;
              return <Badge variant="secondary">{method}</Badge>;
            },
          };

        case 'description':
          return {
            id: 'description',
            header: col.name,
            accessorFn: (r) => r.description,
            cell: ({ row }) => (
              <span className="max-w-[300px] truncate block">{row.original.description}</span>
            ),
          };

        case 'costCentre':
          return {
            id: 'costCentre',
            header: col.name,
            accessorFn: (r) => r.costCentreId ?? '',
            cell: ({ row }) => {
              const cc = row.original.costCentreId;
              if (!cc) return <span className="text-muted-foreground text-sm">—</span>;
              return <Badge variant="outline">{cc}</Badge>;
            },
          };

        case 'status':
          return {
            id: 'status',
            header: col.name,
            enableSorting: false,
            accessorFn: (r) => r.approved ? 'Approved' : 'Pending',
            cell: ({ row }) => {
              const expense = row.original;
              return expense.approved ? (
                <Badge
                  variant="success"
                  accent="success"
                  className="gap-1 cursor-pointer hover:opacity-80"
                  onClick={(e) => { e.stopPropagation(); setApprovalHistoryExpense(expense); setApprovalHistorySheetOpen(true); }}
                >
                  <CheckCircle2 className="h-3 w-3" />Approved
                </Badge>
              ) : (
                <Badge
                  variant="warning"
                  accent="warning"
                  className="gap-1 cursor-pointer hover:opacity-80"
                  onClick={(e) => { e.stopPropagation(); setApprovalHistoryExpense(expense); setApprovalHistorySheetOpen(true); }}
                >
                  <Clock className="h-3 w-3" />Pending
                </Badge>
              );
            },
          };

        case 'net':
          return {
            id: 'net',
            header: col.name,
            accessorFn: (r) => r.amount - (r.taxAmount ?? 0),
            cell: ({ row }) => {
              const net = row.original.amount - (row.original.taxAmount ?? 0);
              return (
                <span className="text-right font-medium block">
                  {formatCurrency(net)}
                </span>
              );
            },
            meta: { align: 'right' },
          };

        case 'vat':
          return {
            id: 'vat',
            header: col.name,
            accessorFn: (r) => r.taxAmount ?? 0,
            cell: ({ row }) => {
              const vat = row.original.taxAmount;
              if (vat === undefined || vat === null) return <span className="text-muted-foreground text-sm">—</span>;
              return (
                <span className="text-right font-medium block">
                  {formatCurrency(vat)}
                </span>
              );
            },
            meta: { align: 'right' },
          };

        case 'vatCode':
          return {
            id: 'vatCode',
            header: col.name,
            accessorFn: (r) => r.taxCodeId ?? '',
            cell: ({ row }) => {
              const code = row.original.taxCodeId;
              if (!code) return <span className="text-muted-foreground text-sm">—</span>;
              return <span className="text-sm font-mono">{code}</span>;
            },
          };

        case 'amount':
          return {
            id: 'amount',
            header: col.name,
            accessorFn: (r) => r.amount,
            cell: ({ row }) => <span className="font-medium">{formatCurrency(row.original.amount)}</span>,
            meta: { align: 'right' },
          };

        case 'receipt':
          return {
            id: 'receipt',
            header: col.name,
            enableSorting: false,
            accessorFn: (r) => (r.receiptUrl || r.attachments?.length) ? 'Attached' : 'None',
            cell: ({ row }) => {
              const expense = row.original;
              return (expense.receiptUrl || (expense.attachments && expense.attachments.length > 0)) ? (
                <Badge variant="outline" className="gap-1">
                  <FileText className="h-3 w-3" />Attached
                </Badge>
              ) : (
                <span className="text-muted-foreground text-sm">—</span>
              );
            },
          };

        default:
          return {
            id: col.id,
            header: col.name,
            accessorFn: () => '',
            cell: () => null,
          };
      }
    });
  }, [visibleColumns, expenses, employees]);

  // ── Export to Excel ───────────────────────────────────────────────────

  const handleExportExcel = useCallback(() => {
    const colWidths: Record<string, number> = {
      date:        12,
      employee:    24,
      type:        20,
      category:    20,
      description: 36,
      status:      14,
      net:         14,
      vat:         14,
      vatCode:     12,
      amount:      14,
      receipt:     10,
    };

    const getValue = (col: ColumnDefinition, e: Expense): string | number => {
      switch (col.id) {
        case 'date':        return format(new Date(e.date), 'dd/MM/yyyy');
        case 'employee':    return getEmployeeName(e);
        case 'type':        return e.category ?? '';
        case 'category':    return e.paymentMethod ?? '';
        case 'description': return e.description ?? '';
        case 'status':      return e.approved ? 'Approved' : 'Pending';
        case 'net':         return e.amount - (e.taxAmount ?? 0);
        case 'costCentre':  return e.costCentreId ?? '';
        case 'vat':         return e.taxAmount ?? '';
        case 'vatCode':     return e.taxCodeId ?? '';
        case 'amount':      return e.amount;
        case 'receipt':     return (e.receiptUrl || (e.attachments && e.attachments.length > 0)) ? 'Yes' : 'No';
        default:            return '';
      }
    };

    const rows = sorted.map((e) =>
      Object.fromEntries(visibleColumns.map((col) => [col.name, getValue(col, e)]))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = visibleColumns.map((col) => ({ wch: colWidths[col.id] ?? 16 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');

    const safeName = (projectCode ?? `project-${projectId}`).replace(/[^a-z0-9]/gi, '-');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Expenses-${safeName}-${today}.xlsx`);
  }, [sorted, visibleColumns, projectCode, projectId, employees]);

  // ── Early returns ─────────────────────────────────────────────────────

  // Expense integration not configured
  if (isExpenseConfigured === false) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Link2Off className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Expense Integration Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  The Expense integration has not been set up for this company. Please configure the integration in Settings to view expense data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (expenses.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Receipt className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Expenses</h3>
                <p className="text-sm text-muted-foreground">
                  There are no expenses associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeFilterCount = countActiveExpenseFilters(advancedFilters);

  return (
    <div className="space-y-6">
      {/* Compact Financial Summary Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CompactKpiCard
          title="Total Expenses"
          value={formatCurrency(summary.totalAmount)}
          description={`${summary.totalCount} expense${summary.totalCount !== 1 ? 's' : ''} logged`}
          icon={Banknote}
          accentVariant="info"
          className="shadow-none"
        />
        <CompactKpiCard
          title="Approved"
          value={formatCurrency(summary.approvedAmount)}
          description={`${summary.approvedCount} expense${summary.approvedCount !== 1 ? 's' : ''} approved`}
          icon={CheckCircle2}
          accentVariant="success"
          className="shadow-none"
        />
        <CompactKpiCard
          title="Awaiting Approval"
          value={formatCurrency(summary.pendingAmount)}
          description={`${summary.pendingCount} expense${summary.pendingCount !== 1 ? 's' : ''} pending`}
          icon={CircleDashed}
          accentVariant="warning"
          className="shadow-none"
        />
        <CompactKpiCard
          title="Team Members"
          value={summary.uniqueEmployees}
          description="Submitting expenses"
          icon={Users}
          accentVariant="info"
          className="shadow-none"
        />
      </div>

      {/* Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Expense Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Toolbar + results count */}
          <div className="px-4 pt-4 pb-3">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
              {/* Search */}
              <div className="relative w-52 shrink-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search expenses..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-8 h-8 text-sm"
                />
              </div>

              {/* Quick filter — Status */}
              <div className="w-40 shrink-0">
                <MultiSelect
                  options={STATUS_OPTIONS}
                  placeholder="Status"
                  value={quickStatusFilter}
                  onValueChange={setQuickStatusFilter}
                  maxDisplay={1}
                  size="sm"
                />
              </div>

              {/* Quick filter — Employee */}
              <div className="w-44 shrink-0">
                <MultiSelect
                  options={EMPLOYEE_OPTIONS}
                  placeholder="Employee"
                  value={quickEmployeeFilter}
                  onValueChange={setQuickEmployeeFilter}
                  maxDisplay={1}
                  size="sm"
                />
              </div>

              {/* Quick filter — Type */}
              <div className="w-44 shrink-0">
                <MultiSelect
                  options={TYPE_OPTIONS}
                  placeholder="Type"
                  value={quickTypeFilter}
                  onValueChange={setQuickTypeFilter}
                  maxDisplay={1}
                  size="sm"
                />
              </div>

              {/* Clear quick filters — only shown when active */}
              {(quickStatusFilter.length > 0 || quickEmployeeFilter.length > 0 || quickTypeFilter.length > 0) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs text-muted-foreground shrink-0"
                  onClick={() => { setQuickStatusFilter([]); setQuickEmployeeFilter([]); setQuickTypeFilter([]); }}
                >
                  Clear
                </Button>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Group By */}
              <div className="w-44 shrink-0">
                <MultiSelect
                  options={EX_GROUP_BY_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
                  placeholder="Group by..."
                  value={groupBy ? [groupBy] : []}
                  onValueChange={(v) => {
                    setGroupBy((v[v.length - 1] ?? '') as EXGroupById | '');
                    setCollapsedGroups(new Set());
                  }}
                  maxDisplay={1}
                  size="sm"
                />
              </div>

              {/* Advanced Filter */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 relative shrink-0"
                onClick={() => setFilterSheetOpen(true)}
                aria-label="Advanced filters"
              >
                <Filter className="h-3.5 w-3.5" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              {/* Column configurator */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                onClick={() => setColumnConfigOpen(true)}
                aria-label="Configure columns"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </Button>

              {/* Export to Excel */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                aria-label="Export to Excel"
                onClick={handleExportExcel}
                disabled={sorted.length === 0}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
          </div>
          {/* Results count */}
          <p className="text-sm text-muted-foreground mb-3">
            {groupedData
              ? `${groupedData.length} group${groupedData.length !== 1 ? 's' : ''} · ${sorted.length} expense${sorted.length !== 1 ? 's' : ''}`
              : `Showing ${paginatedExpenses.length} of ${sorted.length} expense${sorted.length !== 1 ? 's' : ''}`}
          </p>
          </div>

          {/* Grouped view */}
          {groupedData ? (
            <div className="space-y-3">
              {groupedData.map((group) => {
                const isCollapsed = collapsedGroups.has(group.key);
                const ChevronIcon = isCollapsed ? ChevronRight : ChevronDown;
                return (
                  <div key={group.key} className="border rounded-lg overflow-hidden">
                    {/* Group header */}
                    <button
                      className="w-full flex items-center gap-3 px-4 py-2.5 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
                      onClick={() => toggleGroup(group.key)}
                    >
                      <ChevronIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm flex-1">{group.label}</span>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground shrink-0">
                        <span>{group.subtotals.count} expense{group.subtotals.count !== 1 ? 's' : ''}</span>
                        <span className="font-medium text-foreground">
                          {formatCurrency(group.subtotals.totalAmount)}
                        </span>
                        {group.subtotals.pendingAmount > 0 && (
                          <span className="text-warning font-medium">
                            {formatCurrency(group.subtotals.pendingAmount)} pending
                          </span>
                        )}
                        {group.subtotals.approvedAmount > 0 && (
                          <span className="text-success font-medium">
                            {formatCurrency(group.subtotals.approvedAmount)} approved
                          </span>
                        )}
                      </div>
                    </button>
                    {/* Group rows */}
                    {!isCollapsed && (
                      <DataTableEnhanced<Expense, unknown>
                        columns={columnDefs}
                        data={group.expenses}
                        columnVisibility={Object.fromEntries(visibleColumns.map((c) => [c.id, true]))}
                        columnSorts={columnSorts}
                        onColumnSortChange={(s) => { setColumnSorts(s); setCurrentPage(1); }}
                        emptyMessage={
                          <div className="flex flex-col items-center gap-2 py-2 text-muted-foreground">
                            <Receipt className="h-8 w-8" />
                            <p className="text-sm">No expenses in this group</p>
                          </div>
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* Flat table view */
            <DataTableEnhanced<Expense, unknown>
              className="rounded-none"
              data={paginatedExpenses}
              columns={columnDefs}
              columnVisibility={Object.fromEntries(visibleColumns.map((c) => [c.id, true]))}
              columnSorts={columnSorts}
              onColumnSortChange={(newSorts) => { setColumnSorts(newSorts); setCurrentPage(1); }}
              emptyMessage={
                <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                  <Receipt className="h-8 w-8" />
                  <p>No expenses match your filters</p>
                </div>
              }
              paginationContent={
                <PaginationControls
                  pagination={pagination}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                />
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!selectedExpense} onOpenChange={(open) => { if (!open) setSelectedExpense(null); }}>
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          {selectedExpense && (() => {
            // ── Navigation ───────────────────────────────────────────
            const currentIndex  = allSortedExpenses.findIndex((e) => e.id === selectedExpense.id);
            const canPrev       = currentIndex > 0;
            const canNext       = currentIndex < allSortedExpenses.length - 1;
            const handlePrev    = () => canPrev && setSelectedExpense(allSortedExpenses[currentIndex - 1]);
            const handleNext    = () => canNext && setSelectedExpense(allSortedExpenses[currentIndex + 1]);

            // ── Financial values ─────────────────────────────────────
            const net       = selectedExpense.taxAmount != null
              ? selectedExpense.amount - selectedExpense.taxAmount
              : selectedExpense.amount;
            const vat       = selectedExpense.taxAmount ?? 0;
            const gross     = selectedExpense.amount;

            // ── Payment method icon ──────────────────────────────────
            const pm = selectedExpense.paymentMethod?.toLowerCase() ?? '';
            const PaymentIcon = pm.includes('card') || pm.includes('credit') || pm.includes('corporate')
              ? CreditCard
              : Wallet;

            return (
              <>
                {/* ── Header ─────────────────────────────────────────── */}
                <SheetHeader className="px-6 pt-6 pb-4 border-b">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Receipt className="h-4 w-4 text-muted-foreground shrink-0" />
                        <SheetTitle className="text-base">Expense Details</SheetTitle>
                      </div>
                      <SheetDescription className="space-y-1 pt-0.5">
                        <div className="text-sm text-muted-foreground">
                          {format(new Date(selectedExpense.date), 'EEEE, dd MMMM yyyy')}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                          <span>Internal ID: <span className="font-mono font-medium text-foreground">{selectedExpense.id}</span></span>
                          {selectedExpense.externalExpenseId && (
                            <span>Expense ID: <span className="font-mono font-medium text-foreground">{selectedExpense.externalExpenseId}</span></span>
                          )}
                          {selectedExpense.sourceSystem && (
                            <span>Source: <span className="font-medium text-foreground">{selectedExpense.sourceSystem}</span></span>
                          )}
                        </div>
                      </SheetDescription>
                    </div>

                    {/* Prev / Next navigation */}
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {currentIndex + 1}/{allSortedExpenses.length}
                      </span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handlePrev} disabled={!canPrev} title="Previous (↑)">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNext} disabled={!canNext} title="Next (↓)">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

                  {/* ── Financial snapshot strip ────────────────────────── */}
                  <div className="grid grid-cols-3 gap-px bg-border rounded-lg overflow-hidden">
                    {[
                      { label: 'Net Amount',  value: formatCurrency(net)   },
                      { label: 'VAT',         value: vat > 0 ? formatCurrency(vat) : '—' },
                      { label: 'Gross Total', value: formatCurrency(gross) },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-muted/40 px-4 py-3 text-center">
                        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                        <p className="text-sm font-semibold">{value}</p>
                      </div>
                    ))}
                  </div>

                  {/* ── Status badges ───────────────────────────────────── */}
                  <div className="flex flex-wrap gap-2">
                    {selectedExpense.approved ? (
                      <Badge variant="success" accent="success" className="gap-1"><CheckCircle2 className="h-3 w-3" />Approved</Badge>
                    ) : (
                      <Badge variant="warning" accent="warning" className="gap-1"><Clock className="h-3 w-3" />Pending Approval</Badge>
                    )}
                    {selectedExpense.reimbursable && (
                      <Badge variant="outline" className="gap-1"><Banknote className="h-3 w-3" />Reimbursable</Badge>
                    )}
                    {selectedExpense.locked && (
                      <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Period Locked{selectedExpense.lockReason ? ` — ${selectedExpense.lockReason}` : ''}</Badge>
                    )}
                    {selectedExpense.policyViolation && (
                      <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />Policy Violation</Badge>
                    )}
                  </div>

                  {/* ── Entry Details ────────────────────────────────────── */}
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Entry Details</h4>
                    <div className="border rounded-lg divide-y">

                      {/* Employee / Type */}
                      <div className="grid grid-cols-2 divide-x">
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Employee</label>
                          <p className="mt-0.5 text-sm font-medium">{getEmployeeName(selectedExpense)}</p>
                        </div>
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Type</label>
                          <p className="mt-0.5">
                            <Badge variant="outline" className="text-xs">{selectedExpense.category}</Badge>
                          </p>
                        </div>
                      </div>

                      {/* Payment Method / Merchant */}
                      <div className="grid grid-cols-2 divide-x">
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Category</label>
                          {selectedExpense.paymentMethod ? (
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <PaymentIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <p className="text-sm font-medium">{selectedExpense.paymentMethod}</p>
                            </div>
                          ) : (
                            <p className="mt-0.5 text-sm text-muted-foreground">—</p>
                          )}
                        </div>
                        <div className="px-4 py-3">
                          <label className="text-xs text-muted-foreground">Merchant</label>
                          <p className="mt-0.5 text-sm font-medium">
                            {selectedExpense.merchantName ?? <span className="text-muted-foreground">—</span>}
                          </p>
                        </div>
                      </div>

                      {/* VAT Code / Currency */}
                      {(selectedExpense.taxCodeId || selectedExpense.currency) && (
                        <div className="grid grid-cols-2 divide-x">
                          {selectedExpense.taxCodeId && (
                            <div className="px-4 py-3">
                              <label className="text-xs text-muted-foreground">VAT Code</label>
                              <p className="mt-0.5 text-sm font-medium font-mono">{selectedExpense.taxCodeId}</p>
                            </div>
                          )}
                          {selectedExpense.currency && (
                            <div className="px-4 py-3">
                              <label className="text-xs text-muted-foreground">Currency</label>
                              <p className="mt-0.5 text-sm font-medium">{selectedExpense.currency}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Description */}
                      <div className="px-4 py-3">
                        <label className="text-xs text-muted-foreground">Description</label>
                        <p className="mt-0.5 text-sm">{selectedExpense.description || <span className="text-muted-foreground">No description</span>}</p>
                      </div>

                    </div>
                  </div>

                  {/* ── Policy Violation ─────────────────────────────────── */}
                  {selectedExpense.policyViolation && selectedExpense.policyViolationReason && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg space-y-0.5">
                      <p className="text-xs font-semibold text-destructive uppercase tracking-wider">Policy Violation</p>
                      <p className="text-sm text-destructive">{selectedExpense.policyViolationReason}</p>
                    </div>
                  )}

                  {/* ── Reviewer Comment ─────────────────────────────────── */}
                  {selectedExpense.reviewerComment && (
                    <div className="p-3 bg-muted rounded-lg space-y-0.5">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Reviewer Comment</p>
                      <p className="text-sm">{selectedExpense.reviewerComment}</p>
                    </div>
                  )}

                  {/* ── Attachments ──────────────────────────────────────── */}
                  {(selectedExpense.receiptUrl || (selectedExpense.attachments && selectedExpense.attachments.length > 0)) && (
                    <div className="space-y-1">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Attachments
                        {selectedExpense.attachments && selectedExpense.attachments.length > 0 && (
                          <span className="ml-2 normal-case font-normal">({selectedExpense.attachments.length})</span>
                        )}
                      </h4>
                      <div className="space-y-2">
                        {selectedExpense.receiptUrl && (
                          <Button variant="outline" size="sm" className="w-full justify-start" asChild>
                            <a href={selectedExpense.receiptUrl} target="_blank" rel="noopener noreferrer">
                              <Receipt className="h-4 w-4 mr-2" />
                              View Receipt
                              <ExternalLink className="h-3 w-3 ml-auto" />
                            </a>
                          </Button>
                        )}
                        {selectedExpense.attachments?.map((attachment) => (
                          <Button key={attachment.id} variant="outline" size="sm" className="w-full justify-start" asChild>
                            <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                              <FileText className="h-4 w-4 mr-2" />
                              {attachment.fileName}
                              <ExternalLink className="h-3 w-3 ml-auto" />
                            </a>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Approval History (inline preview) ───────────────── */}
                  {selectedExpense.approvalHistory && selectedExpense.approvalHistory.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Approval History
                          <span className="ml-2 normal-case font-normal text-muted-foreground">
                            ({selectedExpense.approvalHistory.length} {selectedExpense.approvalHistory.length === 1 ? 'event' : 'events'})
                          </span>
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs px-2"
                          onClick={() => {
                            setApprovalHistoryExpense(selectedExpense);
                            setApprovalHistorySheetOpen(true);
                          }}
                        >
                          View full history
                        </Button>
                      </div>
                      <ApprovalHistoryTimeline
                        approvalHistory={selectedExpense.approvalHistory}
                        timeEntryId={String(selectedExpense.id)}
                      />
                    </div>
                  )}

                  {/* ── External link ────────────────────────────────────── */}
                  {selectedExpense.externalUrl && (
                    <div className="pt-2">
                      <Button className="w-full" variant="outline" asChild>
                        <a href={selectedExpense.externalUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                          <ExternalLink className="h-4 w-4" />
                          View in Expense System
                        </a>
                      </Button>
                    </div>
                  )}

                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Approval History Sheet / Dialog */}
      {approvalHistoryExpanded ? (
        <Dialog open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>Approval History for Expense</DialogTitle>
                  <DialogDescription>
                    {approvalHistoryExpense && (
                      <>{format(new Date(approvalHistoryExpense.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryExpense)}</>
                    )}
                  </DialogDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setApprovalHistoryExpanded(false)} title="Return to sheet view">
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryExpense?.approvalHistory && (
                <ApprovalHistoryTimeline approvalHistory={approvalHistoryExpense.approvalHistory} timeEntryId={String(approvalHistoryExpense.id)} />
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
            <SheetHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>Approval History for Expense</SheetTitle>
                  <SheetDescription>
                    {approvalHistoryExpense && (
                      <>{format(new Date(approvalHistoryExpense.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryExpense)}</>
                    )}
                  </SheetDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setApprovalHistoryExpanded(true)} title="Expand to full view">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryExpense?.approvalHistory && (
                <ApprovalHistoryTimeline approvalHistory={approvalHistoryExpense.approvalHistory} timeEntryId={String(approvalHistoryExpense.id)} />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Column Configurator */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={expenseColumns}
        onSave={updateColumns}
        pageKey="expense-entries"
      />

      {/* Advanced Filter Sheet */}
      <ExpenseEntriesFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={advancedFilters}
        onApply={(f) => { setAdvancedFilters(f); setCurrentPage(1); }}
        onClear={() => { setAdvancedFilters({}); setCurrentPage(1); }}
        availableEmployees={availableEmployees}
        availableTypes={availableTypes}
        availableCategories={availableCategories}
        availableVatCodes={availableVatCodes}
        availableCostCentres={availableCostCentres}
      />
    </div>
  );
}
